/* pure adapters for the session page's penalties (GET /penalties/session). */
import {
  EM_DASH,
  api_error,
  is_permission_error,
  month_label
} from '../../teacher_payments/format';
import {
  default_pcts_map,
  free_pass_view,
  row_view,
  selection_total_text,
  who_text
} from '../../teacher_payments/penalties/adapters';
import { wire_month } from '../../teacher_payments/penalties/requests';
import { app_join_line } from '../../teacher_payments/shared/app_join';

// "FINISHED" -> "Finished", "STUDENT_NO_SHOW" -> "Student no show" (sentence case, never shouted).
const sentence = (code) => {
  const t = String(code || '')
    .replace(/_/g, ' ')
    .toLowerCase();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
};

export const session_month = (start_ist) => {
  const m = String(start_ist || '').match(/^(\d{4}-\d{2})-\d{2}/);
  return m ? m[1] : '';
};

export const HISTORY_ACTION_LABELS = {
  system_applied: 'Applied by the system',
  add: 'Added',
  correct: 'Corrected',
  reclassify: 'Reclassified',
  waive: 'Waived',
  unwaive: 'Waiver undone',
  revert_override: 'Reverted to the system value',
  remove: 'Removed'
};

export const session_view = (data) => {
  const d = data || {};
  const s = d.session || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map(row_view);
  const names = {};
  rows.forEach((r) => {
    names[r.teacher_uuid] = r.teacher_name;
  });
  const pay_month = session_month(s.start_ist);
  return {
    session_uuid: s.uuid || '',
    // No dangling " · " when a part is missing.
    context_text: [
      s.uuid ? `Session ${s.uuid}` : '',
      s.start_ist ? `${s.start_ist} IST` : '',
      s.duration_mins ? `${s.duration_mins} mins` : '',
      sentence(s.status)
    ]
      .filter(Boolean)
      .join(' · '),
    pay_month,
    month_text: month_label(pay_month),
    rows,
    charged_text: selection_total_text(rows),
    history: (Array.isArray(d.history) ? d.history : []).map((h, i) => ({
      key: `${h.penalty_uuid || ''}|${h.at_ist || ''}|${h.action || ''}|${i}`,
      at_text: h.at_ist ? `${h.at_ist} IST` : EM_DASH,
      by_text: who_text(h.actor_name, h.actor),
      teacher_name: who_text(names[h.teacher_uuid], h.teacher_uuid),
      action_text: HISTORY_ACTION_LABELS[h.action] || sentence(h.action),
      summary: h.summary || '',
      reason: h.reason || ''
    })),
    issues: Array.isArray(d.issues) ? d.issues : [],
    // A 1-on-1's app Join tap for the review pop-up, or "could not be checked"; '' when none.
    app_join_text: app_join_line(s.student_app_join_ist, s.app_join_unavailable === true),
    teacher_names: names,
    free_pass: Array.isArray(d.free_pass) ? d.free_pass : [],
    // same live-policy map as the tab's summary, so the session Add dialog prefills identically.
    default_pcts: default_pcts_map(d.default_pcts)
  };
};

// The row's teacher-month free-pass state from the same payload (display only; the write re-validates).
export const pass_state_for = (view, row) => {
  const hit = view.free_pass.find(
    (f) =>
      f.teacher_uuid === row.teacher_uuid && wire_month(f.pay_month) === wire_month(row.pay_month)
  );
  return hit ? free_pass_view(hit) : null;
};

/*
 * Whether a failed session read hides the strip. No grant always hides. A 404 hides only on the
 * first read and only when it means "nothing here": SESSION_NOT_FOUND, or a codeless 404 from a
 * server image without the module. A 404 on the re-read after a write is an error, not "none".
 */
export const hides_strip = (e, after_write = false) => {
  if (is_permission_error(e)) return true;
  const status = e && e.response && e.response.status;
  if (status !== 404 || after_write) return false;
  const { code } = api_error(e);
  return !code || code === 'SESSION_NOT_FOUND';
};

export const POPUP_TITLE = 'Penalties on this class';
export const POPUP_EMPTY_TEXT = 'No penalties on this class.';

// The pop-up's count line; a failed read shows only the error, never the last rows as current.
export const popup_summary_text = (status, view) => {
  if (status !== 'ok' || !view || !view.rows.length) return '';
  const n = view.rows.length;
  return `${n} ${n === 1 ? 'penalty' : 'penalties'} · charged ${view.charged_text}`;
};

// "View penalties here" waits for the first read, so Ops without the payments grant never see it.
export const shows_view_here = ({ session_uuid, hidden, status }) =>
  !!session_uuid && !hidden && (status === 'ok' || status === 'error');
